import { exitImpersonation, isImpersonating } from "@app/lib/admin-session";
import { environment } from "@app/relay/relay-environment";
import { useCallback, useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { accessGate_MeQuery } from "@app/queries/accessGate_MeQuery.graphql";
import type { accessGate_RevealMutation } from "@app/queries/accessGate_RevealMutation.graphql";

const meQuery = graphql`
  query accessGate_MeQuery {
    me {
      accessGrantedAt
      dataRevealedAt
      impersonated
    }
  }
`;

const revealMutation = graphql`
  mutation accessGate_RevealMutation {
    accountDataReveal {
      user {
        dataRevealedAt
      }
      errors {
        message
      }
    }
  }
`;

type Me = accessGate_MeQuery["response"]["me"];

// Gates the authed app behind the "Process my files" reveal for granted-but-not-
// yet-revealed customers, and shows an impersonation banner for admins acting as
// a user. The operator's own account (never "granted") is never gated.
export default function AccessGate({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | undefined>(undefined);
  const [revealing, setRevealing] = useState(false);

  const reload = useCallback(() => {
    fetchQuery<accessGate_MeQuery>(environment, meQuery, {})
      .toPromise()
      .then((d) => setMe(d?.me ?? null))
      .catch(() => setMe(null));
  }, []);

  useEffect(reload, [reload]);

  const reveal = () => {
    setRevealing(true);
    commitMutation<accessGate_RevealMutation>(environment, {
      mutation: revealMutation,
      variables: {},
      onCompleted: () => {
        setRevealing(false);
        reload();
      },
      onError: () => setRevealing(false),
    });
  };

  if (me === undefined) return null;

  const impersonating = Boolean(me?.impersonated) || isImpersonating();
  const gated = Boolean(me?.accessGrantedAt) && !me?.dataRevealedAt && !impersonating;

  return (
    <>
      {impersonating && (
        <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-sm font-medium text-white">
          <span>You're impersonating this account as staff. Activity is logged.</span>
          <button
            onClick={() => {
              exitImpersonation();
              window.location.assign("/ops-console-7x2k");
            }}
            className="rounded bg-white/20 px-3 py-1 hover:bg-white/30"
          >
            Exit impersonation
          </button>
        </div>
      )}

      {gated ? (
        <div className="flex min-h-screen flex-col items-center justify-center bg-[#0b1e3f] px-4 text-center text-white">
          <h1 className="text-3xl font-bold">Your account is ready</h1>
          <p className="mt-3 max-w-md text-white/70">
            We've received your statements. Click below to process your files and unlock your finance workspace.
          </p>
          <button
            onClick={reveal}
            disabled={revealing}
            className="mt-8 rounded-lg bg-white px-6 py-3 font-semibold text-[#0b1e3f] disabled:opacity-50"
          >
            {revealing ? "Processing your files…" : "Process my files"}
          </button>
        </div>
      ) : (
        children
      )}
    </>
  );
}
